import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RelatedOutages = ({ relatedOutages, serviceProvider }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'text-success bg-success/10';
      case 'investigating':
        return 'text-warning bg-warning/10';
      case 'major-outage':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return 'CheckCircle';
      case 'investigating':
        return 'AlertTriangle';
      case 'major-outage':
        return 'XCircle';
      default:
        return 'Clock';
    }
  };

  return (
    <div className="space-y-6">
      {/* Service Provider Info */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Building" size={24} className="text-primary" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">{serviceProvider?.name}</h3>
            <p className="text-muted-foreground mb-4">{serviceProvider?.description}</p>
            
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" asChild>
                <a href={serviceProvider?.statusPage} target="_blank" rel="noopener noreferrer">
                  <Icon name="ExternalLink" size={14} className="mr-2" />
                  Official Status
                </a>
              </Button>
              
              <Button variant="outline" size="sm" asChild>
                <a href={serviceProvider?.supportUrl} target="_blank" rel="noopener noreferrer">
                  <Icon name="HelpCircle" size={14} className="mr-2" />
                  Support
                </a>
              </Button>
              
              <Button variant="outline" size="sm" asChild>
                <a href={`https://twitter.com/${serviceProvider?.twitter}`} target="_blank" rel="noopener noreferrer">
                  <Icon name="Twitter" size={14} className="mr-2" />
                  @{serviceProvider?.twitter}
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Provider Stats */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{serviceProvider?.reliability}%</p>
              <p className="text-sm text-muted-foreground">30-day Reliability</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{serviceProvider?.avgResolution}</p>
              <p className="text-sm text-muted-foreground">Avg Resolution</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">{serviceProvider?.totalIncidents}</p>
              <p className="text-sm text-muted-foreground">Total Incidents</p>
            </div>
          </div>
        </div>
      </div>
      {/* Related Outages */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Related Outages</h3>
              <p className="text-sm text-muted-foreground">Similar incidents from other services</p>
            </div>
            <Button variant="outline" size="sm">
              <Icon name="Filter" size={14} className="mr-2" />
              Filter
            </Button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {relatedOutages?.map((outage) => (
            <Link
              key={outage?.id}
              to={`/service-outage-details?id=${outage?.id}`}
              className="block p-6 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Wifi" size={16} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{outage?.serviceName}</h4>
                      <p className="text-sm text-muted-foreground">{outage?.serviceType}</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{outage?.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Users" size={14} />
                      <span>{outage?.reports?.toLocaleString()} reports</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="MapPin" size={14} />
                      <span>{outage?.regions} regions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={14} />
                      <span>{outage?.duration}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusColor(outage?.status)}`}>
                    <Icon name={getStatusIcon(outage?.status)} size={12} />
                    <span className="capitalize">{outage?.status?.replace('-', ' ')}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{outage?.lastUpdated}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="p-6 border-t border-border text-center">
          <Button variant="outline">
            <Icon name="ChevronDown" size={16} className="mr-2" />
            Load More Related Outages
          </Button>
        </div>
      </div>
      {/* Trending Services */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Trending Outages</h3>
        <div className="space-y-3">
          {[
            { name: 'Netflix Streaming', reports: 1247, trend: 'up' },
            { name: 'AWS East Coast', reports: 892, trend: 'up' },
            { name: 'Spotify Music', reports: 634, trend: 'down' },
            { name: 'Discord Voice', reports: 421, trend: 'up' }
          ]?.map((service, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Activity" size={16} className="text-primary" />
                </div>
                <span className="font-medium text-foreground">{service?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{service?.reports?.toLocaleString()}</span>
                <Icon 
                  name={service?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                  size={14} 
                  className={service?.trend === 'up' ? 'text-error' : 'text-success'} 
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RelatedOutages;